import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from "lucide-react";

const PopupContext = createContext(null);

const TYPE_CONFIG = {
  success: { icon: CheckCircle, bg: "rgba(20,184,166,0.95)", border: "#14B8A6" },
  error:   { icon: AlertCircle, bg: "rgba(255,107,107,0.95)", border: "#FF6B6B" },
  warning: { icon: AlertTriangle, bg: "rgba(245,158,11,0.95)", border: "#F59E0B" },
  info:    { icon: Info, bg: "rgba(14,165,233,0.95)", border: "#0EA5E9" },
};

export const PopupProvider = ({ children }) => {
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);

  const showPopup = useCallback((message, type = "info") => {
    setPopup({ message, type });
    setVisible(true);
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => setPopup(null), 300);
  }, []);

  useEffect(() => {
    if (!popup) return;
    const timer = setTimeout(dismiss, 3000);
    return () => clearTimeout(timer);
  }, [popup, dismiss]);

  const config = popup ? TYPE_CONFIG[popup.type] || TYPE_CONFIG.info : null;
  const Icon = config?.icon;

  return (
    <PopupContext.Provider value={showPopup}>
      {children}
      {popup && (
        <div
          className="popup-overlay"
          style={{ opacity: visible ? 1 : 0 }}
          onClick={dismiss}
          data-testid="popup-overlay"
        >
          <div
            className="popup-message"
            style={{
              background: config.bg,
              borderColor: config.border,
              transform: visible ? "translate(-50%,-50%) scale(1)" : "translate(-50%,-50%) scale(0.85)",
              opacity: visible ? 1 : 0,
            }}
            onClick={(e) => { e.stopPropagation(); dismiss(); }}
            data-testid="popup-message"
          >
            {Icon && <Icon size={28} className="popup-icon" />}
            <span className="popup-text">{popup.message}</span>
            <button className="popup-close" aria-label="Close"><X size={18} /></button>
          </div>
        </div>
      )}
    </PopupContext.Provider>
  );
};

export const usePopup = () => {
  const ctx = useContext(PopupContext);
  if (!ctx) throw new Error("usePopup must be used within PopupProvider");
  return ctx;
};
